import React from 'react';

import { ButtonGroup, ButtonGroupItem } from '@epoch-ui/components/ButtonGroup';
import { Checkbox } from '@epoch-ui/components/Checkbox';
import { RadioGroup, RadioGroupItem } from '@epoch-ui/components/RadioGroup/RadioGroup';
import { Label } from '@epoch-ui/components/Form/label';
import { Input } from '@epoch-ui/components/Input/input';
import { Question, QUESTION_TYPE } from 'types/question.types';

export const QuestionPreview = ({ question }: { question: Question }): JSX.Element | null => {
  const {
    type,
    label: text,
    isRequired: required,
    options = [],
    hasOtherOption,
    properties
  } = question;
  const { scaleMinValue = 1, scaleMaxValue = 5, scaleMinLabel, scaleMaxLabel } = properties || {};

  switch (type) {
    case QUESTION_TYPE.CHECK: {
      return (
        <div key={question.id} className="tw-mb-6">
          <Label>
            {text}
            {required && <span className="tw-text-primary-blue">*</span>}
          </Label>
          <div className="tw-mt-4 tw-space-y-2">
            {options.map((option, index) => (
              <div
                key={index}
                className="tw-flex tw-h-5 tw-items-center tw-gap-3"
                data-test="question-option"
              >
                <Checkbox className="tw-mt-0" />
                <span className="tw-text-sm tw-text-dark-black">{option}</span>
              </div>
            ))}
            {hasOtherOption && (
              <div
                className="tw-flex tw-h-5 tw-items-center tw-gap-3"
                data-test="question-other-option"
              >
                <Checkbox className="tw-mt-0" />
                <span className="tw-text-sm tw-text-dark-black">Other:</span>
                <Input className="tw-rounded-none tw-h-5 tw-border-0 tw-border-b tw-border-solid tw-border-border-grey" />
              </div>
            )}
          </div>
        </div>
      );
    }
    case QUESTION_TYPE.RADIO: {
      return (
        <div key={question.id} className="tw-mb-6">
          <Label>
            {text}
            {required && <span className="tw-text-primary-blue">*</span>}
          </Label>
          <RadioGroup className="tw-mt-4">
            {options.map((option, index) => (
              <RadioGroupItem key={index} value={option} className="tw-flex tw-h-5 tw-items-center">
                {option}
              </RadioGroupItem>
            ))}
            {hasOtherOption && (
              <div className="tw-flex tw-h-5 tw-items-center tw-gap-3">
                <RadioGroupItem value="other" className="tw-flex tw-items-center">
                  Other:
                </RadioGroupItem>
                <Input className="tw-rounded-none tw-h-5 tw-border-0 tw-border-b tw-border-solid tw-border-border-grey" />
              </div>
            )}
          </RadioGroup>
        </div>
      );
    }
    case QUESTION_TYPE.LINEAR_SCALE:
    case QUESTION_TYPE.NPS:
    case QUESTION_TYPE.SATISFACTION:
    case QUESTION_TYPE.CONNECTION:
    case QUESTION_TYPE.RETENTION: {
      const range = Array.from(
        { length: scaleMaxValue - scaleMinValue + 1 },
        (_, index) => index + scaleMinValue
      );
      return (
        <div className="tw-mb-6 last:tw-mb-0" key={question.id} data-test="question">
          <Label>
            {text}
            {required && <span className="tw-text-primary-blue">*</span>}
          </Label>
          <div className="tw-mt-2 tw-inline-flex tw-w-full tw-flex-col">
            <ButtonGroup className="tw-flex tw-w-full tw-gap-1">
              {range.map((value) => (
                <ButtonGroupItem
                  key={value}
                  value={String(value)}
                  className="tw-aspect-square tw-flex-1 tw-p-0 tw-text-sm"
                >
                  {String(value)}
                </ButtonGroupItem>
              ))}
            </ButtonGroup>
            {(scaleMinLabel || scaleMaxLabel) && (
              <div className="tw-mt-3 tw-flex tw-items-center tw-justify-between modal:tw-flex-col modal:tw-items-start">
                <span className="tw-mr-2 tw-text-xs tw-font-normal tw-leading-4 tw-text-primary-grey">
                  <span className="tw-hidden modal:tw-inline">
                    {' '}
                    {scaleMinLabel && `${scaleMinValue}:`}{' '}
                  </span>
                  {scaleMinLabel}
                </span>
                <span className="tw-mr-2.5 tw-text-xs tw-font-normal tw-leading-4 tw-text-primary-grey">
                  <span className="tw-hidden modal:tw-inline">
                    {' '}
                    {scaleMaxLabel && `${scaleMaxValue}:`}{' '}
                  </span>
                  {scaleMaxLabel}
                </span>
              </div>
            )}
          </div>
        </div>
      );
    }
    case QUESTION_TYPE.LONG: {
      return (
        <div key={question.id} className="tw-mb-6">
          <Label>
            {text}
            {required && <span className="tw-text-primary-blue">*</span>}
          </Label>
          <Input className="tw-mt-4" />
        </div>
      );
    }
    default:
      return null;
  }
};
